<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import InquiryService from '@/service/inquiry.service';

const name = ref('');
const email = ref('');
const company = ref('');
const phone = ref('');
const message = ref('');
const loading = ref(false);
const success = ref(false);
const error = ref('');

const root = ref<HTMLElement | null>(null);

async function submit() {
  error.value = '';
  success.value = false;
  if (!name.value || !email.value) {
    error.value = 'Please provide your name and email';
    return;
  }
  loading.value = true;
  try {
    await InquiryService.createPartnerInquiry({
      name: name.value,
      email: email.value,
      company: company.value || undefined,
      phone: phone.value || undefined,
      message: message.value || undefined,
    });
    success.value = true;
    name.value = '';
    email.value = '';
    company.value = '';
    phone.value = '';
    message.value = '';
  } catch (e: any) {
    error.value = e?.message || 'Failed to submit inquiry';
  }
  loading.value = false;
}
onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('show');
        observer.unobserve(entry.target);
      }
    });
  }, {
    root: null,
    threshold: 0.15,
    rootMargin: '0px 0px -10% 0px',
  });

  const elements = root.value?.querySelectorAll('.anim');
  elements?.forEach((el) => observer.observe(el));

  onUnmounted(() => observer.disconnect());
});
</script>

<template>
  <section ref="root" class="relative overflow-visible bg-white py-20" id="become-partner">
    <div class="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#DBCC8B]/40 blur-3xl animate-pulse"></div>
    <div class="absolute -bottom-20 -left-10 w-96 h-96 rounded-full bg-[#BC8809]/30 blur-3xl animate-pulse"></div>

    <div class="mx-auto max-w-6xl px-6">
      <div class="text-center mb-10">
        <div class="inline-block border border-black/10 py-1 px-4 rounded-full text-sm tracking-wide anim slide-up">Partners</div>
        <h2 class="mt-4 text-4xl md:text-5xl font-bold text-gray-900 anim slide-up">Become a partner</h2>
        <p class="mt-3 text-gray-600 anim slide-up">Join TALAB’s premium network and access a high-intent student audience.</p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-10 items-stretch">
        <div class="bg-white rounded-2xl shadow-xl p-8 border border-black/5 anim slide-right">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="text-sm text-gray-700">Full name</label>
              <input v-model="name" type="text" class="mt-1 w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#C4992A] transition" placeholder="Jane Doe" />
            </div>
            <div>
              <label class="text-sm text-gray-700">Email</label>
              <input v-model="email" type="email" class="mt-1 w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#C4992A] transition" placeholder="jane@company.com" />
            </div>
            <div>
              <label class="text-sm text-gray-700">Company</label>
              <input v-model="company" type="text" class="mt-1 w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#C4992A] transition" placeholder="Company Ltd" />
            </div>
            <div>
              <label class="text-sm text-gray-700">Phone</label>
              <input v-model="phone" type="tel" class="mt-1 w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#C4992A] transition" placeholder="+44 ..." />
            </div>
            <div class="md:col-span-2">
              <label class="text-sm text-gray-700">Message</label>
              <textarea v-model="message" rows="4" class="mt-1 w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#C4992A] transition" placeholder="Tell us about your services and goals..."></textarea>
            </div>
          </div>
          <div class="mt-5 flex items-center gap-3">
            <button @click="submit" :disabled="loading" class="px-6 py-3 rounded-xl bg-[#C4992A] text-white font-semibold hover:bg-[#DBCC8B] hover:text-black transition disabled:opacity-60">
              <span v-if="!loading">Send inquiry</span>
              <svg v-else aria-hidden="true" class="w-6 h-6 text-gray-200 animate-spin fill-white" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/></svg>
            </button>
            <span v-if="success" class="text-green-600">Thank you! We’ll get back to you shortly.</span>
            <span v-if="error" class="text-red-600">{{ error }}</span>
          </div>
        </div>
        <div class="relative anim slide-left bg-black-primary text-white rounded-2xl p-8 overflow-hidden flex items-center justify-center">
          <div class="absolute inset-0 opacity-20" style="background-image: radial-gradient(circle at 20% 20%, #DBCC8B 0, transparent 40%), radial-gradient(circle at 80% 30%, #C4992A 0, transparent 40%), radial-gradient(circle at 50% 80%, #BC8809 0, transparent 40%);"></div>
          <div class="relative z-10 text-center">
            <h3 class="text-3xl font-bold mb-3">Grow with TALAB</h3>
            <p class="text-gray-300 max-w-md mx-auto">Connect with a premium audience and showcase your services inside our app. We partner with brands that care about quality.</p>
            <div class="mt-6 flex items-center justify-center gap-3">
              <div class="h-2 w-2 rounded-full bg-[#DBCC8B] animate-bounce"></div>
              <div class="h-2 w-2 rounded-full bg-[#C4992A] animate-bounce" style="animation-delay:0.15s"></div>
              <div class="h-2 w-2 rounded-full bg-[#BC8809] animate-bounce" style="animation-delay:0.3s"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.anim { opacity: 0; transform: translateY(12px); transition: all .6s ease; }
.anim.show { opacity: 1; transform: none; }
.slide-up { }
.slide-right { }
.slide-left { }
</style>
